import React, { useLayoutEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { trackEvent } from '@xingternal/hops-tracking';
import { useViewerData } from '@xingternal/hub';
import { IconArrowDown } from '@xingternal/icons';
import { BodyCopy, Meta } from '@xingternal/typography';

import { JobPostingModule } from '@/components/job-posting-module';
import { ScrollTrackingModuleWrapper } from '@/components/job-posting/components/scroll-tracking-module-wrapper';
import { JobSummary } from '@/graphql/generated';
import { trackNwtEvent } from '@/helpers/nwt-tracking';
import { getKey, setKey } from '@/helpers/storage';
import { useBreakpoints } from '@/hooks';

import { JobSummaryContent } from './components/job-summary-content';

import {
  ContentWrapper,
  FeedbackButton,
  JobSummaryCard,
  JobSummaryCardContent,
  JobSummaryExpandButton,
  JobSummaryFeedback,
  JobSummaryHeadline,
  JobSummaryIcon,
  JobSummaryNotice,
  NewFlag,
} from './job-summary-module.styles';

type JobSummaryModuleProps = {
  jobId: string;
  language?: 'de' | 'en';
  summary: JobSummary;
};

export const JobSummaryModule: React.FC<JobSummaryModuleProps> = ({ jobId, language, summary }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [expandedOnce, setExpandedOnce] = useState(false);
  const [feedbackAlreadyGiven, setFeedbackAlreadyGiven] = useState(
    getKey<string[]>('job_ai_summary_feedbacks', []).includes(summary.trackingToken)
  );
  const { data } = useViewerData();
  const userId = data?.user?.id;
  const { isMobile } = useBreakpoints();

  const baseNwtProps = {
    user_id: userId,
    channel: isMobile ? 'webm' : 'web',
    item_urn: `surn:x-xing:jobs:posting:${jobId}`,
    tracking_token: summary.trackingToken,
    page: 'jobs/detail',
    element: 'job_ai_summary',
  };

  useLayoutEffect(() => {
    trackNwtEvent({
      ...baseNwtProps,
      event: 'viewed',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (): void => {
    // If we are collapsing, we set expandedOnce to true so that next time we
    // expand, we don't see the animation.
    // The order here is important because if we need to setExpandedOnce only
    // when collapsing. Doing it also on expansion would cause the animation to
    // not be shown.
    if (!collapsed && !expandedOnce) setExpandedOnce(true);
    setCollapsed(!collapsed);

    const action = `job_ai_summary_${collapsed ? 'expand' : 'collapse'}`;

    trackEvent('PropTrackAction', {
      PropTrackAction: `jobs_${action}`,
    });

    trackNwtEvent({
      ...baseNwtProps,
      event: 'clicked',
      element_detail: action,
    });
  };

  const sendFeedback = (feedback: 'like' | 'unlike'): void => {
    trackEvent('PropTrackAction', {
      PropTrackAction: `jobs_job_ai_summary_${feedback}`,
    });

    trackNwtEvent({
      ...baseNwtProps,
      event: 'feedback_sent',
      element_detail: `job_ai_summary_${feedback}d`,
    });

    setKey<string[]>(`job_ai_summary_feedbacks`, [
      ...getKey<string[]>(`job_ai_summary_feedbacks`, []),
      summary.trackingToken,
    ]);
    setFeedbackAlreadyGiven(true);
  };

  return (
    <ScrollTrackingModuleWrapper moduleTrackingName="Summary">
      <JobPostingModule data-testid="job-summary">
        <JobSummaryCard>
          <JobSummaryCardContent onClick={() => collapsed && toggle()} $collapsed={collapsed}>
            <NewFlag size="small" variant="new" />
            <JobSummaryIcon width={18} height={18} />
            <JobSummaryHeadline size="large" noMargin>
              <FormattedMessage id="JOBS_DETAIL_AI_SUMMARY_HEADER" />
            </JobSummaryHeadline>
            <ContentWrapper>
              {collapsed ? (
                <BodyCopy size="medium" ellipsis noMargin>
                  {summary.content.introduction}
                </BodyCopy>
              ) : (
                <JobSummaryContent
                  language={language}
                  skipAnimation={expandedOnce}
                  summary={summary}
                  onAnimationEnd={() => setExpandedOnce(true)}
                />
              )}
            </ContentWrapper>
            {!collapsed ? (
              <>
                <JobSummaryNotice>
                  <Meta size="medium" noMargin>
                    <FormattedMessage id="JOBS_DETAIL_AI_SUMMARY_NOTICE" />
                  </Meta>
                </JobSummaryNotice>
                {!feedbackAlreadyGiven && expandedOnce ? (
                  <JobSummaryFeedback>
                    <Meta size="medium" noMargin>
                      <FormattedMessage id="JOBS_DETAIL_AI_SUMMARY_FEEDBACK" />
                    </Meta>
                    <FeedbackButton $action="like" onClick={() => sendFeedback('like')} data-testid="feedback-like" />
                    <FeedbackButton
                      $action="unlike"
                      onClick={() => sendFeedback('unlike')}
                      data-testid="feedback-unlike"
                    />
                  </JobSummaryFeedback>
                ) : null}
              </>
            ) : null}
          </JobSummaryCardContent>
          <JobSummaryExpandButton
            icon={IconArrowDown}
            onClick={toggle}
            data-testid="summary-expand-button"
            $collapsed={collapsed}
          />
        </JobSummaryCard>
      </JobPostingModule>
    </ScrollTrackingModuleWrapper>
  );
};
